<template>
  <div class="my-6">
    <div v-if="loading" class="d-flex justify-center pa-12">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>
    
    <div v-else-if="data">
      <!-- Main Metrics Row -->
      <v-row class="mb-8">
        <v-col cols="12" md="4">
          <v-card outlined class="modern-card pa-6 h-100 shadow-sm border-left-primary">
            <div class="d-flex justify-space-between align-start mb-4">
              <div>
                <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1">Total Venta</div>
                <div class="text-h3 font-weight-black primary--text lh-1">{{data.sales_total | moneda}}</div>
              </div>
              <v-avatar color="primary lighten-5" size="44" class="shadow-xs">
                <v-icon color="primary">fas fa-hand-holding-usd</v-icon>
              </v-avatar>
            </div>
            <div class="caption grey--text font-weight-medium">Ingreso bruto facturado / cotizado</div>
          </v-card>
        </v-col>
        
        <v-col cols="12" md="4">
          <v-card outlined class="modern-card pa-6 h-100 shadow-sm" :class="getMarginBorderClass(1 - data.sales_cost/data.sales_total)">
            <div class="d-flex justify-space-between align-start mb-4">
              <div>
                <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1">Margen Teórico</div>
                <div class="d-flex align-end">
                  <div class="text-h3 font-weight-black lh-1" :class="getMarginTextClass(1 - data.sales_cost/data.sales_total)">{{(1 - data.sales_cost/data.sales_total) | percent(1)}}</div>
                  <div class="text-subtitle-1 grey--text font-weight-bold ml-2 pb-0.5">/ {{data.sales_cost | moneda}}</div>
                </div>
              </div>
              <v-avatar :color="getMarginLightColor(1 - data.sales_cost/data.sales_total)" size="44" class="shadow-xs">
                <v-icon :color="getMarginColor(1 - data.sales_cost/data.sales_total)">fas fa-chart-line</v-icon>
              </v-avatar>
            </div>
            <div class="caption grey--text font-weight-medium">Basado en costos de cotización (teórico)</div>
          </v-card>
        </v-col>
        
        <v-col cols="12" md="4">
          <v-card outlined class="modern-card pa-6 h-100 shadow-sm bg-accent-tint" :class="getMarginBorderClass(1 - data.total/data.sales_total)">
            <div class="d-flex justify-space-between align-start mb-4">
              <div>
                <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1">Margen Real</div>
                <div class="d-flex align-end">
                  <div class="text-h3 font-weight-black lh-1" :class="getMarginTextClass(1 - data.total/data.sales_total)">{{(1 - data.total/data.sales_total) | percent(1)}}</div>
                  <div class="text-subtitle-1 grey--text font-weight-bold ml-2 pb-0.5">/ {{data.total | moneda}}</div>
                </div>
              </div>
              <v-avatar :color="getMarginLightColor(1 - data.total/data.sales_total)" size="44" class="shadow-sm border">
                <v-icon :color="getMarginColor(1 - data.total/data.sales_total)">fas fa-wallet</v-icon>
              </v-avatar>
            </div>
            <div class="caption grey--text font-weight-medium">Incluye costos reales y consumos a la fecha</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Detailed Costs Table -->
      <v-card outlined class="modern-card overflow-hidden shadow-sm">
        <v-sheet color="grey lighten-4" class="px-6 py-4 border-bottom d-flex align-center">
          <v-icon small color="primary" class="mr-3">fas fa-list-ul</v-icon>
          <span class="text-subtitle-2 font-weight-bold grey--text text--darken-2 text-uppercase tracking-wider">Desglose de Costos Reales</span>
        </v-sheet>
        
        <v-simple-table class="modern-table">
          <template v-slot:default>
            <thead>
              <tr class="bg-grey-light">
                <th class="text-left font-weight-black grey--text text--darken-3 py-4 uppercase tracking-widest caption">Concepto de Costo</th>
                <th class="text-right font-weight-black grey--text text--darken-3 py-4 uppercase tracking-widest caption" width="200">Monto Registrado</th>
                <th class="text-left font-weight-black grey--text text--darken-3 py-4 uppercase tracking-widest caption">Descripción / Fuente</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="font-weight-bold py-4 text-left">Costos de Producción</td>
                <td class="text-right font-weight-black primary--text py-4">{{data.production_cost | moneda}}</td>
                <td class="caption grey--text font-weight-medium py-4 text-left">Registros de bitácora y horas máquina</td>
              </tr>
              <tr>
                <td class="font-weight-bold py-4 text-left">Costo de Reprocesos</td>
                <td class="text-right font-weight-black error--text py-4">{{data.reprocess_cost | moneda}}</td>
                <td class="caption grey--text font-weight-medium py-4 text-left">Costos adicionales por fallos o correcciones</td>
              </tr>
              <tr>
                <td class="font-weight-bold py-4 text-left">Consumo Directo</td>
                <td class="text-right font-weight-black secondary--text py-4">{{data.consume_cost | moneda}}</td>
                <td class="caption grey--text font-weight-medium py-4 text-left">Materiales consumidos en planta</td>
              </tr>
              <tr>
                <td class="font-weight-bold py-4 text-left">Consumo en Reproceso</td>
                <td class="text-right font-weight-black orange--text text--darken-3 py-4">{{data.consume_cost_reprocess | moneda}}</td>
                <td class="caption grey--text font-weight-medium py-4 text-left">Materiales usados en reprocesos</td>
              </tr>
              <tr>
                <td class="font-weight-bold py-4 text-left">Consumo de Bodega</td>
                <td class="text-right font-weight-black indigo--text py-4">{{data.warehouse_consumption | moneda}}</td>
                <td class="caption grey--text font-weight-medium py-4 text-left">Salidas de inventario vinculadas</td>
              </tr>
              <tr>
                <td class="font-weight-bold py-4 text-left">Costos Adicionales</td>
                <td class="text-right font-weight-black deep-purple--text py-4">{{data.additional_costs | moneda}}</td>
                <td class="caption grey--text font-weight-medium py-4 text-left">Fletes, servicios externos, etc.</td>
              </tr>
              <tr>
                <td class="font-weight-bold py-4 text-left">Órdenes de Compra</td>
                <td class="text-right font-weight-black blue-grey--text py-4">{{data.purchase_orders_costs | moneda}}</td>
                <td class="caption grey--text font-weight-medium py-4 text-left">OC directas a proveedores</td>
              </tr>
              <tr class="bg-primary-light">
                <td class="font-weight-black text-uppercase tracking-wider primary--text py-6 text-left">Total Costo Real Acumulado</td>
                <td class="text-right font-weight-black primary--text text-h5 py-6">{{data.total | moneda}}</td>
                <td class="py-6"></td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-card>
    </div>
    
    <div v-else class="text-center pa-12 grey--text">
      <v-icon x-large class="mb-4 opacity-20">fas fa-chart-pie</v-icon>
      <div class="text-subtitle-1 font-weight-bold">No hay datos disponibles</div>
      <div class="caption opacity-70 mt-1">No se encontró el resumen de costos para esta OT.</div>
    </div>
  </div>
</template>

<script>
import {getWorkOrderCostResume} from '@/api/production.js'
import notifications from '@/utils/notifications' 

export default {
  name: 'RunningCost',
  props: ['work_order'],
  data() {
    return {
      loading: false,
    }
  },
  computed: {
    data() {
      return this.$store.getters.getworkOrderCostResume
    }
  },
  created() {
    if (this.work_order) {
      this.getData()
    }
  },
  methods: {
    async getData() {
      this.loading = true
      await this.$store.dispatch('fetchWorkOrderResume', this.work_order.id)
      .catch(e => {
        notifications.showError(e)
      })
      .finally(() => {
        this.loading = false
      })
    },
    getMarginBorderClass(margin) {
      if (margin <= 0) return 'border-left-error'
      if (margin < 0.2) return 'border-left-warning'
      return 'border-left-success'
    },
    getMarginColor(margin) {
      if (margin <= 0) return 'error'
      if (margin < 0.2) return 'orange darken-2'
      return 'success'
    },
    getMarginLightColor(margin) {
      if (margin <= 0) return 'error lighten-5'
      if (margin < 0.2) return 'orange lighten-5'
      return 'success lighten-5'
    },
    getMarginTextClass(margin) {
      if (margin <= 0) return 'error--text'
      if (margin < 0.2) return 'orange--text text--darken-2'
      return 'success--text'
    }
  }
}
</script>

<style scoped>
.modern-card { 
  border: 1px solid rgba(0,0,0,0.08) !important; 
  border-radius: 12px !important; 
  background-color: #fff;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}
.modern-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 20px rgba(0,0,0,0.08) !important;
}

.border-left-primary { border-left: 5px solid var(--v-primary-base) !important; }
.border-left-success { border-left: 5px solid var(--v-success-base) !important; }
.border-left-warning { border-left: 5px solid var(--v-warning-base) !important; }
.border-left-error { border-left: 5px solid var(--v-error-base) !important; }

.bg-grey-light { background-color: #f8f9fa; }
.bg-primary-light { background-color: rgba(var(--v-primary-base), 0.05); }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.08); }

.modern-table { background-color: transparent !important; }
.modern-table th { white-space: nowrap; }
.modern-table td { transition: background 0.2s; }
.modern-table tr:hover td { background-color: rgba(0,0,0,0.01); }

.uppercase { text-transform: uppercase; }
.tracking-widest { letter-spacing: 2px; }
.tracking-wider { letter-spacing: 1px; }
.lh-1 { line-height: 1; }
.opacity-20 { opacity: 0.2; }
.opacity-70 { opacity: 0.7; }
.shadow-sm { box-shadow: 0 2px 4px rgba(0,0,0,0.05) !important; }
.shadow-xs { box-shadow: 0 1px 2px rgba(0,0,0,0.03) !important; }
</style>